import React, { useEffect, useMemo, useRef, useState } from 'react'
import { IconButton } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { FiInfo, FiX } from 'react-icons/fi'
import ModalFlexOverflow from '../../../../components/Template/Modal/ModalFlexOverflow'
import { NumberInput, Radio } from '../../../../components/FormBuilder'
import Button from '../../../../components/Template/Button'
import { INewIndependentBookingPlan } from '../../../../models/Booking/getNewIndependentBookingPlans'
import {
  applyBookingRateToPlanGroup,
  areAllBookingPlanRatesFilled,
  formatBookingPlanCommissionPercent,
  getGroupedBookingPlanRateValue,
  groupNamedBookingPlans,
  IGroupedNamedBookingPlan,
  INamedBookingPlan,
  normalizeBookingPlanRatesForSave,
} from '../../../../models/Price/groupBookingPlanRatesByName'
import bookingLogo from '../../assets/Booking.com_Icon2022.png'
import {
  AnnouncementBox,
  AnnouncementText,
  ButtonWrapper,
  CommissionModalHeadingBlock,
  CommissionModalHeadingTop,
  CommissionModalSubtitle,
  CommissionModalTitle,
  Content,
  DescText,
  DescTitle,
  EditablePlan,
  EditablePlanRows,
  Header,
  LogoImg,
  OptionsSection,
  PlanLeft,
  PlanName,
  PlanRow,
  PricesSentSection,
  PricesSentTitle,
  RadioDesct,
  RadioWrapper,
  ReadOnlyPlanName,
  ReadOnlyPlanPercent,
  ReadOnlyPlanRow,
  RecommendedBadge,
  Text,
  Wrapper,
  YesOptionTitle,
  YesOptionTitleRow,
} from './sharedStyled'

type CommissionPlanRow = {
  group: IGroupedNamedBookingPlan<INamedBookingPlan>
  readOnly: boolean
}

function BookingYesCommissionContent({
  t,
  editableRows,
  readOnlyRows,
  planRates,
  bookingConfiguredRates,
  changePlanRate,
}: {
  t: (key: string) => string
  editableRows: CommissionPlanRow[]
  readOnlyRows: CommissionPlanRow[]
  planRates: Record<string, number | string>
  bookingConfiguredRates?: Record<string, number | string>
  changePlanRate: (planIds: string[]) => (v: string | number) => void
}) {
  return (
    <OptionsSection>
      <YesOptionTitleRow>
        <YesOptionTitle>
          {t('BED_13698_popup_global_commision_yes')}
        </YesOptionTitle>
        <RecommendedBadge>
          {t('booking_com_prices_sent_recommended_badge')}
        </RecommendedBadge>
      </YesOptionTitleRow>
      <DescText
        style={{ marginBottom: editableRows.length > 0 ? '10px' : '0' }}
      >
        {t('booking_com_prices_sent_description')}
      </DescText>
      <EditablePlan>
        <EditablePlanRows>
          {editableRows.map(({ group }, planIndex) => (
            <PlanRow
              key={group.key}
              style={{ marginTop: planIndex === 0 ? '10px' : '16px' }}
            >
              <PlanLeft>
                <LogoImg alt="" src={bookingLogo} />
                <PlanName>{group.label}</PlanName>
              </PlanLeft>
              <div style={{ flex: '1 1 180px', maxWidth: '220px' }}>
                <NumberInput.Field
                  autoConvert={true}
                  decimal={2}
                  inputStyle={{
                    border: '1px solid #FFA800',
                    maxWidth: '200px',
                  }}
                  isSmallReservation={true}
                  label={t('BED_11802_price_increased_by')}
                  labelStyle={{
                    color: '#858585',
                    fontFamily: 'Montserrat',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                  maxValueForNumber={999.99}
                  minValueForNumber={0}
                  onlyInteger={false}
                  onChange={changePlanRate(group.planIds)}
                  units="%"
                  value={getGroupedBookingPlanRateValue(
                    group.planIds,
                    planRates,
                  )}
                  width="100%"
                  withoutPicker={true}
                  wrapperStyle={{
                    marginTop: '0px',
                    maxWidth: '200px',
                  }}
                />
              </div>
            </PlanRow>
          ))}
        </EditablePlanRows>

        {readOnlyRows.length > 0 && (
          <PricesSentSection>
            <PricesSentTitle>
              {t('booking_com_prices_sent_title')}
            </PricesSentTitle>
            {readOnlyRows.map(({ group }) => (
              <ReadOnlyPlanRow key={group.key}>
                <ReadOnlyPlanName>{group.label}</ReadOnlyPlanName>
                <ReadOnlyPlanPercent>
                  {formatBookingPlanCommissionPercent(
                    getGroupedBookingPlanRateValue(
                      group.planIds,
                      bookingConfiguredRates || {},
                    ),
                  )}
                </ReadOnlyPlanPercent>
              </ReadOnlyPlanRow>
            ))}
          </PricesSentSection>
        )}
      </EditablePlan>
    </OptionsSection>
  )
}

function IntegrationModal({
  open,
  type,
  handleClose,
  handleConnect,
  bookingNewIndependentPlans,
  bookingRoomPlans,
  bookingConfiguredRates,
  bookingRatesNames,
}: IIntegrationModal) {
  const { t } = useTranslation()
  const [value, setValue] = useState('0')
  const [planRates, setPlanRates] = useState<Record<string, number | string>>(
    {},
  )
  const modalSessionOpenRef = useRef(false)

  const isBookingNewPlansMode =
    type === 'Booking' &&
    bookingNewIndependentPlans &&
    bookingNewIndependentPlans.length > 0

  const showBookingYesExtraParagraphs =
    type !== 'Booking' ||
    !bookingNewIndependentPlans ||
    bookingNewIndependentPlans.length !== 0

  useEffect(() => {
    if (!open) {
      modalSessionOpenRef.current = false
      return
    }

    const isFirstOpenInSession = !modalSessionOpenRef.current
    modalSessionOpenRef.current = true

    if (isFirstOpenInSession) {
      setValue('0')
    }

    if (bookingNewIndependentPlans && bookingNewIndependentPlans.length > 0) {
      setPlanRates(prev => {
        const next = isFirstOpenInSession ? {} : { ...prev }
        bookingNewIndependentPlans.forEach(p => {
          const id = String(p.booking_id)
          if (next[id] === undefined) {
            next[id] = 0
          }
        })
        return next
      })
    } else if (isFirstOpenInSession) {
      setPlanRates({})
    }
  }, [open, bookingNewIndependentPlans])

  const connect = () => {
    if (value === '0') {
      const allFilled = areAllBookingPlanRatesFilled(
        editableCommissionRows.map(({ group }) => group.planIds),
        planRates,
      )
      if (!allFilled) return
    }

    if (isBookingNewPlansMode) {
      const normalizedPlanRates = normalizeBookingPlanRatesForSave(planRates)
      handleConnect(value, value === '0' ? normalizedPlanRates : undefined)
    } else {
      handleConnect(value)
    }
  }

  const changeField = (v: string) => () => {
    setValue(v)
  }

  const changePlanRate = (planIds: string[]) => (v: string | number) => {
    setPlanRates(prev => applyBookingRateToPlanGroup(prev, planIds, v))
  }

  const newPlanIds = useMemo(
    () =>
      new Set(
        (bookingNewIndependentPlans || []).map(p => String(p.booking_id)),
      ),
    [bookingNewIndependentPlans],
  )

  const commissionPlanRows = useMemo(() => {
    const plans =
      bookingRoomPlans && bookingRoomPlans.length > 0
        ? bookingRoomPlans
        : bookingNewIndependentPlans || []
    if (!plans.length) return []

    const grouped = groupNamedBookingPlans(plans, plan => {
      const id = String(plan.booking_id)
      const fromNames =
        bookingRatesNames &&
        bookingRatesNames[id] &&
        String(bookingRatesNames[id])
      const fromPricing = plan.name ? String(plan.name) : ''
      return fromNames || fromPricing || id
    })

    return grouped
      .map(group => ({
        group,
        readOnly: !group.planIds.some(id => newPlanIds.has(id)),
      }))
      .sort((a, b) => Number(a.readOnly) - Number(b.readOnly))
  }, [
    bookingRoomPlans,
    bookingNewIndependentPlans,
    bookingRatesNames,
    newPlanIds,
  ])

  const editableCommissionRows = useMemo(
    () => commissionPlanRows.filter(row => !row.readOnly),
    [commissionPlanRows],
  )

  const readOnlyCommissionRows = useMemo(
    () => commissionPlanRows.filter(row => row.readOnly),
    [commissionPlanRows],
  )

  const showBookingCommissionUi = type === 'Booking'

  const canConnect = useMemo(() => {
    if (value !== '0') return true
    return areAllBookingPlanRatesFilled(
      editableCommissionRows.map(({ group }) => group.planIds),
      planRates,
    )
  }, [value, editableCommissionRows, planRates])

  if (isBookingNewPlansMode && bookingNewIndependentPlans) {
    return (
      <ModalFlexOverflow open={open} onClose={handleClose}>
        <Wrapper>
          <CommissionModalHeadingBlock>
            <CommissionModalHeadingTop>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CommissionModalTitle>
                  {t('BED_13698_popup_global_commision_1')}
                </CommissionModalTitle>
                <CommissionModalSubtitle>
                  {t('BED_13698_popup_global_commision_2')}
                </CommissionModalSubtitle>
              </div>
              <IconButton
                onClick={handleClose}
                style={{ flexShrink: 0, margin: '-4px 0 0 0', padding: '8px' }}
              >
                <FiX style={{ fontSize: '20px' }} />
              </IconButton>
            </CommissionModalHeadingTop>
          </CommissionModalHeadingBlock>
          <AnnouncementBox>
            <FiInfo
              style={{
                color: '#973C00',
                flexShrink: 0,
              }}
              size={14}
            />
            <AnnouncementText>
              {t('BED_13698_popup_global_commision_ announcement')}
            </AnnouncementText>
          </AnnouncementBox>
          <Content
            style={{
              marginBottom: '0',
              marginTop: '20px',
              maxHeight: '60vh',
            }}
          >
            <RadioWrapper $selected={value === '0'}>
              <Radio.Field
                changeFieldValue={changeField('0')}
                isHorizontal={true}
                isReverse={true}
                noSpacing={true}
                temporaryValue={'0'}
                value={value}
                wrapperStyle={{ marginTop: '5px', width: 'unset' }}
              />
              <RadioDesct onClick={changeField('0')}>
                <BookingYesCommissionContent
                  bookingConfiguredRates={bookingConfiguredRates}
                  changePlanRate={changePlanRate}
                  editableRows={editableCommissionRows}
                  planRates={planRates}
                  readOnlyRows={readOnlyCommissionRows}
                  t={t}
                />
              </RadioDesct>
            </RadioWrapper>
            <RadioWrapper
              $selected={value === '1'}
              style={{ marginTop: '28px' }}
            >
              <Radio.Field
                changeFieldValue={changeField('1')}
                isHorizontal={true}
                isReverse={true}
                noSpacing={true}
                temporaryValue={'1'}
                value={value}
                wrapperStyle={{ marginTop: '5px', width: 'unset' }}
              />
              <RadioDesct onClick={changeField('1')}>
                <DescTitle>
                  {t('BED_13698_popup_global_commision_no')}
                </DescTitle>
                <DescText>
                  {t('BED_13698_popup_global_commision_no_1')}
                </DescText>
              </RadioDesct>
            </RadioWrapper>
          </Content>
          <ButtonWrapper style={{ marginTop: '34px' }}>
            <Button
              light={true}
              onClick={handleClose}
              size={0.9}
              style={{ color: '#858585' }}
              white={true}
            >
              {t('cancel')}
            </Button>
            <Button
              isDisabled={!canConnect}
              onClick={connect}
              primary={true}
              size={0.9}
              style={{ marginLeft: '5px' }}
            >
              {t('BED_13698_popup_global_commision_connect_room')}
            </Button>
          </ButtonWrapper>
        </Wrapper>
      </ModalFlexOverflow>
    )
  }

  return (
    <ModalFlexOverflow open={open} onClose={handleClose}>
      <Wrapper>
        <Header>
          {t('BED_13698_popup_global_commision_1')}
          <IconButton onClick={handleClose}>
            <FiX style={{ fontSize: '20px' }} />
          </IconButton>
        </Header>
        <Text>
          {t(
            type === 'Airbnb'
              ? 'BED_13699_popup_global_commision_2'
              : 'BED_13698_popup_global_commision_2',
          )}
        </Text>
        <Content style={{ marginTop: '36px', maxHeight: 'none' }}>
          <RadioWrapper $selected={value === '0'}>
            <Radio.Field
              changeFieldValue={changeField('0')}
              isHorizontal={true}
              isReverse={true}
              noSpacing={true}
              temporaryValue={'0'}
              value={value}
              wrapperStyle={{ marginTop: '5px', width: 'unset' }}
            />
            <RadioDesct onClick={changeField('0')}>
              {showBookingCommissionUi ? (
                <BookingYesCommissionContent
                  bookingConfiguredRates={bookingConfiguredRates}
                  changePlanRate={changePlanRate}
                  editableRows={editableCommissionRows}
                  planRates={planRates}
                  readOnlyRows={readOnlyCommissionRows}
                  t={t}
                />
              ) : (
                <>
                  <DescTitle>
                    {t('BED_13698_popup_global_commision_yes')}
                  </DescTitle>
                  {showBookingYesExtraParagraphs && (
                    <>
                      <DescText style={{ marginBottom: '10px' }}>
                        {t(
                          type === 'Airbnb'
                            ? 'BED_13699_popup_global_commision_yes_1'
                            : 'BED_13698_popup_global_commision_yes_1',
                        )}
                      </DescText>
                      <DescText>
                        {t('BED_13698_popup_global_commision_yes_2')}
                      </DescText>
                    </>
                  )}
                </>
              )}
            </RadioDesct>
          </RadioWrapper>
          <RadioWrapper $selected={value === '1'} style={{ marginTop: '36px' }}>
            <Radio.Field
              changeFieldValue={changeField('1')}
              isHorizontal={true}
              isReverse={true}
              noSpacing={true}
              temporaryValue={'1'}
              value={value}
              wrapperStyle={{ marginTop: '5px', width: 'unset' }}
            />
            <RadioDesct onClick={changeField('1')}>
              <DescTitle>
                {t(
                  type === 'Airbnb'
                    ? 'BED_13699_popup_global_commision_no'
                    : 'BED_13698_popup_global_commision_no',
                )}
              </DescTitle>
              <DescText>
                {t(
                  type === 'Airbnb'
                    ? 'BED_13699_popup_global_commision_no_1'
                    : 'BED_13698_popup_global_commision_no_1',
                )}
              </DescText>
            </RadioDesct>
          </RadioWrapper>
        </Content>
        <ButtonWrapper>
          <Button
            light={true}
            onClick={handleClose}
            size={0.9}
            style={{ color: '#858585' }}
            white={true}
          >
            {t('cancel')}
          </Button>
          <Button
            isDisabled={!canConnect}
            onClick={connect}
            primary={true}
            size={0.9}
            style={{ marginLeft: '5px' }}
          >
            {t('BED_13698_popup_global_commision_connect_room')}
          </Button>
        </ButtonWrapper>
      </Wrapper>
    </ModalFlexOverflow>
  )
}

export interface IIntegrationModal {
  open: boolean
  type: 'Booking' | 'Airbnb'
  handleClose: any
  handleConnect: (
    value: string,
    newPlanRates?: Record<string, number | string>,
  ) => void
  /**
   * Booking: lista nowych planów niezależnych — tryb „nowy popup” prowizyjny.
   * Pusta tablica: stary popup (bez bannera nowego planu) z nową sekcją planów.
   */
  bookingNewIndependentPlans?: INewIndependentBookingPlan[]
  /** Wszystkie niezależne plany z podłączanego pokoju (nowe + istniejące). */
  bookingRoomPlans?: INewIndependentBookingPlan[]
  /** Aktualne `integrations.booking.rates` — wartości dla planów już skonfigurowanych. */
  bookingConfiguredRates?: Record<string, number | string>
  /** Nazwy planów z konfiguracji (ratesNames) — podpis przy braku nazwy w pricing. */
  bookingRatesNames?: Record<string, string>
}

export default IntegrationModal
